<script setup>
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import PublicFooter from '@/Components/Marketing/PublicFooter.vue';
import PublicSeoHead from '@/Components/PublicSeoHead.vue';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps({
    locale: { type: String, default: 'en' },
    footerGroups: { type: Array, default: () => [] },
    seo: { type: Object, default: () => ({}) },
});

const isEnglish = computed(() => props.locale === 'en');
const homeHref = computed(() => isEnglish.value ? '/en' : '/');
const switchHref = computed(() => isEnglish.value ? '/suppression-du-compte' : '/account-deletion');
const pageTitle = computed(() => isEnglish.value ? 'Delete Your SportsDock Account' : 'Supprimer votre compte');
const homeLabel = computed(() => isEnglish.value ? 'Back to home' : "Retour à l'accueil");
const switchLabel = computed(() => isEnglish.value ? 'Français' : 'English');
</script>

<template>
    <PublicSeoHead :title="pageTitle" :seo="props.seo" />

    <div class="min-h-screen bg-white text-secondary dark:bg-gray-900 dark:text-gray-100">
        <header class="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div class="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link :href="homeHref" class="flex items-center">
                    <ApplicationLogo class="h-9 w-auto" />
                </Link>
                <div class="flex items-center gap-2 sm:gap-3">
                    <Link
                        :href="switchHref"
                        class="rounded-lg px-3 py-2 text-sm font-medium text-primary transition hover:bg-primary/10"
                    >
                        {{ switchLabel }}
                    </Link>
                    <Link
                        :href="homeHref"
                        class="rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        {{ homeLabel }}
                    </Link>
                </div>
            </div>
        </header>

        <main class="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
            <section v-if="isEnglish" class="prose prose-neutral max-w-none dark:prose-invert">
                <h1>Delete your SportsDock account</h1>
                <p class="lead">
                    SportsDock users can request account deletion directly from the mobile app. This page explains the steps and what happens to your data.
                </p>

                <h2>How to delete your account</h2>
                <ol>
                    <li>Open the SportsDock mobile app.</li>
                    <li>Go to <strong>Profile</strong>, then <strong>Settings</strong>.</li>
                    <li>Select <strong>Delete My Account</strong>.</li>
                    <li>Review the confirmation message and type the requested confirmation word.</li>
                    <li>Confirm deletion.</li>
                </ol>

                <p>
                    Your account is disabled immediately after confirmation. A 30-day grace period applies before permanent deletion, unless we need to retain limited information for legal, security, fraud-prevention, or record-keeping reasons.
                </p>

                <h2>Data deleted</h2>
                <p>After the grace period, we delete or anonymize personal data associated with your account, including:</p>
                <ul>
                    <li>Account identity information such as name, email address, preferred language, and authentication profile.</li>
                    <li>Player or coach profile information, including profile photos, bio, sports preferences, skill level, city, and postal code.</li>
                    <li>Games, templates, team memberships, match or join requests, ratings, reviews, messages, notifications, and device tokens tied to your account where deletion is technically and legally possible.</li>
                </ul>

                <h2>Data we may keep</h2>
                <p>
                    We may keep limited records when required or permitted by law, including records needed for security, fraud prevention, abuse reports, dispute resolution, legal compliance, backups, or audit logs. Backup copies may remain for a limited time until they are overwritten through normal backup rotation.
                </p>

                <h2>Need help?</h2>
                <p>
                    If you cannot access your account or the app, contact us at
                    <a href="mailto:support@sportsdock.ca" class="text-primary hover:underline">support@sportsdock.ca</a>
                    and include the email address associated with your SportsDock account.
                </p>
            </section>

            <section v-else class="prose prose-neutral max-w-none dark:prose-invert">
                <h1>Supprimer votre compte SportsDock</h1>
                <p class="lead">
                    Les utilisateurs de SportsDock peuvent demander la suppression de leur compte directement dans l'application mobile. Cette page explique les étapes et ce qui arrive à vos données.
                </p>

                <h2>Comment supprimer votre compte</h2>
                <ol>
                    <li>Ouvrez l'application mobile SportsDock.</li>
                    <li>Allez dans <strong>Profil</strong>, puis <strong>Paramètres</strong>.</li>
                    <li>Sélectionnez <strong>Supprimer mon compte</strong>.</li>
                    <li>Lisez le message de confirmation et tapez le mot de confirmation demandé.</li>
                    <li>Confirmez la suppression.</li>
                </ol>

                <p>
                    Votre compte est désactivé immédiatement après la confirmation. Une période de grâce de 30 jours s'applique avant la suppression définitive, sauf si nous devons conserver certains renseignements pour des raisons légales, de sécurité, de prévention de la fraude ou de tenue de dossiers.
                </p>

                <h2>Données supprimées</h2>
                <p>Après la période de grâce, nous supprimons ou anonymisons les données personnelles associées à votre compte, notamment :</p>
                <ul>
                    <li>Les renseignements d'identité du compte, comme le nom, l'adresse courriel, la langue préférée et le profil d'authentification.</li>
                    <li>Les renseignements de profil joueur ou coach, y compris les photos de profil, la bio, les préférences sportives, le niveau, la ville et le code postal.</li>
                    <li>Les matchs, modèles, adhésions aux équipes, demandes de match ou de participation, notes, avis, messages, notifications et jetons d'appareil liés à votre compte lorsque la suppression est techniquement et légalement possible.</li>
                </ul>

                <h2>Données que nous pouvons conserver</h2>
                <p>
                    Nous pouvons conserver des dossiers limités lorsque la loi l'exige ou le permet, notamment pour la sécurité, la prévention de la fraude, les signalements d'abus, le règlement de différends, la conformité légale, les sauvegardes ou les journaux d'audit. Des copies de sauvegarde peuvent rester pendant une durée limitée jusqu'à leur remplacement dans le cadre de la rotation normale des sauvegardes.
                </p>

                <h2>Besoin d'aide ?</h2>
                <p>
                    Si vous ne pouvez pas accéder à votre compte ou à l'application, communiquez avec nous à
                    <a href="mailto:support@sportsdock.ca" class="text-primary hover:underline">support@sportsdock.ca</a>
                    en indiquant l'adresse courriel associée à votre compte SportsDock.
                </p>
            </section>
        </main>

        <PublicFooter :groups="footerGroups" :locale="props.locale" />
    </div>
</template>
